import { useEffect, useState, useCallback } from 'react';
import { programsAPI } from '../utils/api';
import { isFreeProgram, ymcaNameOf } from '../utils/programType';

// The current user's program (coach or parent), fetched once per page load
// and shared between layout and pages so every fee/payment surface can
// branch on the same answer (v2.0 F3/F4).
const cache = new Map(); // programId -> program row
const inflight = new Map();
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn());

export const setCachedProgram = (program) => {
  if (program?.id) { cache.set(program.id, program); notify(); }
};

const load = (programId) => {
  if (cache.has(programId)) return Promise.resolve(cache.get(programId));
  if (!inflight.has(programId)) {
    inflight.set(programId, programsAPI.getSettings(programId)
      .then((res) => { const p = res.data.program; cache.set(programId, p); notify(); return p; })
      .catch(() => null)
      .finally(() => inflight.delete(programId)));
  }
  return inflight.get(programId);
};

export default function useProgram(programId) {
  const [, force] = useState(0);
  const [loading, setLoading] = useState(!cache.has(programId));

  useEffect(() => {
    if (!programId) { setLoading(false); return undefined; }
    let active = true;
    const rerender = () => force((n) => n + 1);
    listeners.add(rerender);
    load(programId).then(() => { if (active) setLoading(false); });
    return () => { active = false; listeners.delete(rerender); };
  }, [programId]);

  const program = programId ? cache.get(programId) || null : null;
  const refresh = useCallback(() => { if (programId) { cache.delete(programId); setLoading(true); load(programId).then(() => setLoading(false)); } }, [programId]);

  return {
    program,
    loading,
    programType: program?.program_type || 'paid',
    isFree: isFreeProgram(program?.program_type),
    ymcaName: ymcaNameOf(program),
    refresh,
  };
}

export const __resetProgramCache = () => { cache.clear(); inflight.clear(); };
